import { stableAliasKeys } from '@components/reports/aliasedGridColumns'
import { accountLinkHtml } from './accountLinks'

/**
 * Spec 127 §4.5 (slice S5) — the Balance Sheet page's model.
 *
 * cb2's Balance Sheet is CB's `DownloadBalanceSheetCSV` export, parsed by cb-api into
 * `{ headers, rows, meta }` and, since S1, `hierarchy` (CB's account ids joined onto
 * the CSV row by row — `cb-api/src/domains/reports/balanceSheetHierarchy.js`). The CSV
 * already carries CB's whole structure; ExportHelper.CreateBalanceSheetExport writes
 * it in one fixed layout (ExportHelper.cs:3656, 3740-3831):
 *
 *     [asset sections]            Section = CB's section name on every account row
 *       parent (own amount), children (Parent Account = the parent's NAME),
 *       closer ['', '', 'Total: <parent>', '', <rolled amount>]
 *       'Total: <section>'        the section total, label in column 0
 *     'Grand Total: Assets'
 *     'Liabilities'               a bare side heading, then a header echo
 *     [liability sections]
 *     'Equities'                  a bare side heading, then a header echo
 *     [equity sections]           the NAMELESS combined section ('Total: '), Retained
 *                                 Earnings, Profit & Loss (Net Income, id -999)
 *     'Total: Equity'
 *     'Grand Total: Liabilities & Equity'
 *
 * (The 'Assets' heading lands in `meta`, which parseExportCsv treats as pre-header
 * metadata.) This module reads that layout as it is and REPLACES the page's old
 * row heuristics, which produced three shipped mis-groupings (Credit Card filed under
 * a group titled "Liabilities", a group "Equities" of three blank $0.00 rows, an
 * empty "Equity" group holding "Total: Equity" — spec 127 §2.4, verifier 4).
 *
 * Nothing here computes money. Every figure on screen is a cell CB sent; amounts are
 * parsed only to give export cells a real number (`dataValue`). Anything the layout
 * does not account for makes the model refuse (`ok: false`) and the page shows CB's
 * rows flat, in CSV order, with every column — never a guessed grouping.
 *
 * Pure: no I/O, no React.
 */

// ---------------------------------------------------------------------------
// Cell helpers
// ---------------------------------------------------------------------------

/** CB's hard-coded leading export headers (ExportHelper.cs:3656). */
export const BALANCE_SHEET_BASE_HEADERS = Object.freeze(['Section', 'Account Number', 'Account', 'Parent Account'])

// displayValue / groupHeaderContent are injected as HTML by the grid
// (dangerouslySetInnerHTML) — escape ALL raw data placed in them.
export const esc = (s) => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

const txt = (v) => String(v ?? '').trim()

/**
 * "$1,234.56" / "-$350.00" / "($500.00)" → a number, for a VALUE cell's `dataValue`
 * only (displayed values always come straight from the CSV). Requires a digit:
 * `Number('')` is 0, which once typed every text column as numeric (CC-49944).
 */
export const parseAmount = (v) => {
  if (v == null || String(v).trim() === '') return undefined
  const str = String(v)
  const cleaned = str.replace(/[^0-9.-]/g, '')
  if (!/\d/.test(cleaned)) return undefined
  const n = Number(cleaned)
  if (!Number.isFinite(n)) return undefined
  return str.includes('(') ? -Math.abs(n) : n
}

/** Strict `YYYY-MM-DD` naming a real calendar day (rejects `2026-02-30` and ''). */
export const isValidAsOfDate = (v) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(v ?? ''))
  if (!m) return false
  const y = Number(m[1])
  const mo = Number(m[2])
  const d = Number(m[3])
  const dt = new Date(Date.UTC(y, mo - 1, d))
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === mo - 1 && dt.getUTCDate() === d
}

// ---------------------------------------------------------------------------
// Row classes (spec 127 §4.5), tested IN THIS ORDER, first match wins
// ---------------------------------------------------------------------------

export const ROW = Object.freeze({
  HEADER_ECHO: 'header-echo',
  STATEMENT_TOTAL: 'statement-total',
  SECTION_TOTAL: 'section-total',
  CLOSER: 'closer',
  SIDE_HEADING: 'side-heading',
  PLACEHOLDER: 'placeholder',
  ACCOUNT: 'account',
})

const ASSETS_GRAND_TOTAL = 'Grand Total: Assets'
const LE_GRAND_TOTAL = 'Grand Total: Liabilities & Equity'
const EQUITY_TOTAL = 'Total: Equity'

/** Which statement total a `Grand Total: …` / `Total: Equity` row is, or 'unknown'. */
const statementTotalWhich = (label) => {
  if (label === ASSETS_GRAND_TOTAL) return 'assets'
  if (label === LE_GRAND_TOTAL) return 'liabilities-and-equity'
  if (label === EQUITY_TOTAL) return 'equity'
  return 'unknown'
}

/** `Total: Bank` → 'Bank'; the nameless combined equity section's `Total: ` → ''. */
const sectionNameOf = (label) => txt(label).replace(/^Total:/, '').trim()

/**
 * Classify one positional CSV row. `next` is the following row, because CB's
 * statement-level `Total: Equity` is told apart from a tenant section that happens to
 * be named "Equity" only by position: the statement total is the row immediately
 * before `Grand Total: Liabilities & Equity`.
 */
export const classifyRow = (cells, headers, next) => {
  const c = (i) => txt(cells?.[i])
  const n = headers.length
  if (n > 0 && headers.every((h, i) => c(i) === txt(h))) return ROW.HEADER_ECHO
  const textBlank = c(1) === '' && c(2) === '' && c(3) === ''
  if (/^Grand Total:/.test(c(0)) && textBlank) return ROW.STATEMENT_TOTAL
  if (c(0) === EQUITY_TOTAL && textBlank && txt(next?.[0]) === LE_GRAND_TOTAL) return ROW.STATEMENT_TOTAL
  if (/^Total:/.test(c(0)) && textBlank) return ROW.SECTION_TOTAL
  if (c(0) === '' && c(1) === '' && c(2).startsWith('Total: ') && c(3) === '') return ROW.CLOSER
  if (c(0) !== '' && cells.slice(1, n).every((v) => txt(v) === '')) return ROW.SIDE_HEADING
  if (c(0) === '' && textBlank) {
    // CB's equity placeholder rows (AccountingHelper.Reports.cs:1937-1941): every text
    // cell blank and every value EXACTLY zero. CB blanks a zero category cell
    // (ExportHelper.cs:3842-3851), so blank counts as zero there; the last value
    // (Amount, or Total) must be a real 0 — a blank row is not "$0.00".
    const values = cells.slice(4, n)
    const last = parseAmount(values[values.length - 1])
    if (values.length > 0 && last === 0
      && values.every((v) => txt(v) === '' || parseAmount(v) === 0)) return ROW.PLACEHOLDER
  }
  return ROW.ACCOUNT
}

/** Do these headers have CB's Balance Sheet shape (base four, then Amount, or categories then Total)? */
export const isBalanceSheetHeaders = (headers) => {
  const h = (headers || []).map(txt)
  if (h.length < 5 || BALANCE_SHEET_BASE_HEADERS.some((b, i) => h[i] !== b)) return false
  return h.length === 5 ? h[4] === 'Amount' : h[h.length - 1] === 'Total'
}

// ---------------------------------------------------------------------------
// Roll-up availability
// ---------------------------------------------------------------------------

export const ROLLUP = Object.freeze({ OK: 'ok', NONE: 'none', UNAVAILABLE: 'unavailable' })

/** Page-side reasons (the note's wording is ROLLUP_UNAVAILABLE_TEXT). */
export const ROLLUP_REASON = Object.freeze({
  MISMATCH: 'mismatch',
  UNREADABLE: 'unreadable',
  AS_OF: 'as-of',
  DUPLICATE: 'duplicate',
  SAVED_BEFORE_ROLLUP: 'saved-before-rollup',
})

/** Spec 127 §8 — the reason after "Roll-up is unavailable for this run: ". */
export const ROLLUP_UNAVAILABLE_TEXT = Object.freeze({
  [ROLLUP_REASON.MISMATCH]: "CB's account structure did not match this statement line for line, so no roll-up is offered rather than a guessed one.",
  [ROLLUP_REASON.UNREADABLE]: "CB's account structure could not be read.",
  [ROLLUP_REASON.AS_OF]: 'the as-of date could not be verified.',
  [ROLLUP_REASON.SAVED_BEFORE_ROLLUP]: 'this saved result was produced before roll-up existed.',
  [ROLLUP_REASON.DUPLICATE]: 'CB sent the same account more than once.',
})

// cb-api's refusal codes (balanceSheetHierarchy.js HIERARCHY_REASONS) → the page's
// reasons. Anything not listed — a code added later — reads as "could not be read".
const CB_API_REASON = {
  'as-of-date-not-verifiable': ROLLUP_REASON.AS_OF,
  'as-of-date-mismatch': ROLLUP_REASON.AS_OF,
  'duplicate-account-id': ROLLUP_REASON.DUPLICATE,
  'row-count-mismatch': ROLLUP_REASON.MISMATCH,
  'row-mismatch': ROLLUP_REASON.MISMATCH,
  'indistinguishable-hierarchy': ROLLUP_REASON.MISMATCH,
}

// The hierarchy descriptor kind each row class must carry when status is 'ok'.
const KIND_OF_CLASS = {
  [ROW.HEADER_ECHO]: 'header-echo',
  [ROW.STATEMENT_TOTAL]: 'statement-total',
  [ROW.SECTION_TOTAL]: 'section-total',
  [ROW.CLOSER]: 'rollup',
  [ROW.SIDE_HEADING]: 'side-heading',
  [ROW.PLACEHOLDER]: 'account',
  [ROW.ACCOUNT]: 'account',
}

const isInt = (v) => Number.isInteger(v)

/**
 * The page's own consistency check of cb-api's `hierarchy` (status 'ok') against the
 * CSV it describes (§4.5 A1): one descriptor per row; each row's kind agrees with the
 * page's classification; the section indexes follow the page's own sections; every
 * child's parent is an earlier account of the same section at depth − 1, and has a
 * closer; every closer points back at an earlier account of its section whose
 * descendants are exactly the rows between them and whose name it carries. Returns a
 * page reason, or null when the hierarchy may drive roll-up.
 */
const checkHierarchy = (h, cells, classes, whichOf) => {
  if (!Array.isArray(h) || h.length !== cells.length) return ROLLUP_REASON.MISMATCH
  let section = 0
  for (let i = 0; i < h.length; i++) {
    const d = h[i]
    const cls = classes[i]
    if (!d || d.kind !== KIND_OF_CLASS[cls]) return ROLLUP_REASON.MISMATCH
    if (cls === ROW.PLACEHOLDER && !d.placeholder) return ROLLUP_REASON.MISMATCH
    if (cls === ROW.ACCOUNT && d.placeholder) return ROLLUP_REASON.MISMATCH
    if (cls === ROW.STATEMENT_TOTAL && d.which !== whichOf[i]) return ROLLUP_REASON.MISMATCH
    if (cls === ROW.ACCOUNT || cls === ROW.PLACEHOLDER || cls === ROW.CLOSER || cls === ROW.SECTION_TOTAL) {
      if (d.section !== section) return ROLLUP_REASON.MISMATCH
    }
    if (cls === ROW.SECTION_TOTAL) section += 1
  }
  const byId = new Map()
  for (let i = 0; i < h.length; i++) {
    const d = h[i]
    if (d.kind !== 'account' || d.accountId == null) continue
    if (!isInt(d.accountId)) return ROLLUP_REASON.MISMATCH
    if (byId.has(d.accountId)) return ROLLUP_REASON.DUPLICATE
    byId.set(d.accountId, i)
  }
  const parents = new Set()
  for (let i = 0; i < h.length; i++) {
    const d = h[i]
    if (d.kind !== 'account') continue
    if (!isInt(d.depth) || d.depth < 0) return ROLLUP_REASON.MISMATCH
    const inSectionParent = d.parentAccountId != null && !d.parentElsewhere
    if (!inSectionParent) {
      if (d.depth !== 0) return ROLLUP_REASON.MISMATCH
    } else {
      const p = byId.get(d.parentAccountId)
      if (p == null || p >= i || h[p].section !== d.section || h[p].depth !== d.depth - 1) return ROLLUP_REASON.MISMATCH
      parents.add(p)
    }
    if (d.rollupRow != null) {
      const r = d.rollupRow
      if (!isInt(r) || classes[r] !== ROW.CLOSER || h[r]?.ofRow !== i) return ROLLUP_REASON.MISMATCH
    }
  }
  for (const p of parents) if (h[p].rollupRow == null || h[p].accountId == null) return ROLLUP_REASON.MISMATCH
  for (let i = 0; i < h.length; i++) {
    if (classes[i] !== ROW.CLOSER) continue
    const d = h[i]
    const o = d.ofRow
    if (!isInt(o) || o < 0 || o >= i || classes[o] !== ROW.ACCOUNT) return ROLLUP_REASON.MISMATCH
    const node = h[o]
    if (node.rollupRow !== i || node.accountId == null || d.accountId !== node.accountId) return ROLLUP_REASON.MISMATCH
    if (node.section !== d.section || d.depth !== node.depth) return ROLLUP_REASON.MISMATCH
    if (txt(cells[i][2]) !== `Total: ${txt(cells[o][2])}`) return ROLLUP_REASON.MISMATCH
    for (let k = o + 1; k < i; k++) {
      if (!(classes[k] === ROW.ACCOUNT || classes[k] === ROW.CLOSER) || !(h[k].depth > node.depth)) return ROLLUP_REASON.MISMATCH
    }
  }
  return null
}

// ---------------------------------------------------------------------------
// The model
// ---------------------------------------------------------------------------

/**
 * Classify CB's rows and read the statement's structure.
 *
 * @param {{ headers?: string[], rows?: object[], meta?: string[], hierarchy?: object }} data
 *   the cb-api Balance Sheet payload (live or a stored job result)
 * @param {{ savedResult?: boolean }} [opts] the data is a stored job result — a
 *   missing `hierarchy` then means it was produced before roll-up existed
 * @returns the model: `ok` (the layout was recognised end to end), `empty` (no
 *   data at all — nothing to say), the per-row classes, the Assets / Liabilities /
 *   Equities structure by row index, `hasCloser`, and `rollup` ({ status, reason? }).
 */
export function buildBalanceSheetModel(data, { savedResult = false } = {}) {
  const headers = (data?.headers || []).map((h) => String(h ?? ''))
  const rawRows = data?.rows || []
  const cells = rawRows.map((r) => (Array.isArray(r)
    ? headers.map((_, i) => String(r[i] ?? ''))
    : headers.map((h) => String(r?.[h] ?? ''))))
  const base = { headers, cells, empty: headers.length === 0 && cells.length === 0 }
  const refuse = (failure) => ({ ...base, ok: false, failure, rollup: null })
  if (base.empty) return refuse('empty')
  if (!isBalanceSheetHeaders(headers)) return refuse('unrecognised-headers')

  const classes = cells.map((c, i) => classifyRow(c, headers, cells[i + 1]))
  const whichOf = cells.map((c, i) => (classes[i] === ROW.STATEMENT_TOTAL ? statementTotalWhich(txt(c[0])) : null))

  // CB's own "Assets" heading: the meta entry right after the "As of" line.
  const meta = data?.meta || []
  const asOfAt = meta.findIndex((m) => /^As of\b/.test(txt(m)))
  const assetsHeading = asOfAt >= 0 ? txt(meta[asOfAt + 1]) : ''
  if (!assetsHeading) return refuse('no-assets-heading')

  const assets = { heading: assetsHeading, sections: [], totalIdx: -1 }
  const liabilities = { heading: '', sections: [] }
  const equities = { heading: '', rows: [], sections: [], totalIdx: -1 }
  let grandTotalIdx = -1
  let phase = 'assets'
  let pending = []
  for (let i = 0; i < cells.length; i++) {
    const cls = classes[i]
    const c0 = txt(cells[i][0])
    if (cls === ROW.HEADER_ECHO) continue
    if (cls === ROW.ACCOUNT || cls === ROW.CLOSER || cls === ROW.PLACEHOLDER) {
      if (phase !== 'assets' && phase !== 'liabilities' && phase !== 'equities') return refuse('row-outside-a-section')
      pending.push(i)
      continue
    }
    if (cls === ROW.SECTION_TOTAL) {
      if (phase !== 'assets' && phase !== 'liabilities' && phase !== 'equities') return refuse('section-total-outside-a-side')
      const label = sectionNameOf(c0)
      // Every account row of the section carries the section's name in column 0
      // (closer rows carry none; placeholders are blank, i.e. the nameless section).
      for (const k of pending) {
        if (classes[k] !== ROW.CLOSER && txt(cells[k][0]) !== label) return refuse('section-label-mismatch')
      }
      const section = { label, rows: pending.filter((k) => classes[k] !== ROW.PLACEHOLDER), totalIdx: i }
      if (phase === 'equities') {
        equities.sections.push(section)
        equities.rows.push(...section.rows)
      } else {
        ;(phase === 'assets' ? assets : liabilities).sections.push(section)
      }
      pending = []
      continue
    }
    if (cls === ROW.STATEMENT_TOTAL) {
      const which = whichOf[i]
      if (pending.length) return refuse('rows-without-a-section-total')
      if (which === 'assets' && phase === 'assets') { assets.totalIdx = i; phase = 'after-assets'; continue }
      if (which === 'equity' && phase === 'equities') { equities.totalIdx = i; phase = 'after-equity'; continue }
      if (which === 'liabilities-and-equity' && phase === 'after-equity') { grandTotalIdx = i; phase = 'done'; continue }
      return refuse('unexpected-statement-total')
    }
    // ROW.SIDE_HEADING
    if (pending.length) return refuse('rows-without-a-section-total')
    if (phase === 'after-assets') { liabilities.heading = c0; phase = 'liabilities'; continue }
    if (phase === 'liabilities') { equities.heading = c0; phase = 'equities'; continue }
    return refuse('unexpected-side-heading')
  }
  if (phase !== 'done') return refuse('statement-incomplete')

  const hasCloser = classes.includes(ROW.CLOSER)
  const hierarchy = data?.hierarchy
  let rollup
  if (hierarchy == null) {
    rollup = { status: ROLLUP.UNAVAILABLE, reason: savedResult ? ROLLUP_REASON.SAVED_BEFORE_ROLLUP : ROLLUP_REASON.UNREADABLE }
  } else if (hierarchy.status === ROLLUP.NONE) {
    // cb-api answers 'none' only when it found no closer; one here is a contradiction.
    rollup = hasCloser ? { status: ROLLUP.UNAVAILABLE, reason: ROLLUP_REASON.MISMATCH } : { status: ROLLUP.NONE }
  } else if (hierarchy.status === ROLLUP.UNAVAILABLE) {
    rollup = { status: ROLLUP.UNAVAILABLE, reason: CB_API_REASON[hierarchy.reason] || ROLLUP_REASON.UNREADABLE }
  } else if (hierarchy.status === ROLLUP.OK) {
    const reason = checkHierarchy(hierarchy.rows, cells, classes, whichOf)
    rollup = reason ? { status: ROLLUP.UNAVAILABLE, reason } : { status: ROLLUP.OK, rows: hierarchy.rows }
  } else {
    rollup = { status: ROLLUP.UNAVAILABLE, reason: ROLLUP_REASON.UNREADABLE }
  }

  return {
    ...base,
    ok: true,
    classes,
    assets,
    liabilities,
    equities,
    grandTotalIdx,
    hasCloser,
    rollup,
  }
}

/** Should the page say why roll-up is missing? Only when there was a parent to roll up. */
export const rollupNoteReason = (model) => (
  model?.ok && model.hasCloser && model.rollup?.status === ROLLUP.UNAVAILABLE ? model.rollup.reason : null
)

// ---------------------------------------------------------------------------
// The grid report
// ---------------------------------------------------------------------------

const TEXT_HEADERS = new Set(BALANCE_SHEET_BASE_HEADERS)

/**
 * The flat fallback: CB's rows exactly as sent, in CSV order, with EVERY column
 * (Section included), no groups and no footer. The known text headers are always
 * text — Account Number above all (CC-53904: "0315" stays "0315", "10101" never
 * becomes "10,101"); any other column counts as an amount column only when most of
 * its cells parse as money, so an export still gets real numbers for real amounts.
 */
function fallbackReport(model, accountsIndex) {
  const { headers, cells } = model
  const aliasKeys = stableAliasKeys(headers)
  const valueCol = headers.map((h, i) => {
    if (TEXT_HEADERS.has(txt(h))) return false
    let seen = 0
    let numeric = 0
    for (const r of cells) {
      const v = txt(r[i])
      if (!v || v === txt(h)) continue
      seen += 1
      if (parseAmount(v) != null) numeric += 1
    }
    return seen > 0 && numeric / seen >= 0.7
  })
  const nameIdx = headers.findIndex((h) => txt(h) === 'Account')
  const numIdx = headers.findIndex((h) => txt(h) === 'Account Number')
  const columns = headers.map((h, i) => ({
    columnId: `c${i}`,
    aliasKey: aliasKeys[i],
    label: h,
    visible: true,
    columnAlignment: valueCol[i] ? 'RIGHT' : 'LEFT',
    ...(TEXT_HEADERS.has(txt(h)) ? { filterTypeName: 'TEXT' } : {}),
  }))
  const reportData = cells.map((r) => ({
    isGroup: false,
    dataCells: r.map((raw, i) => {
      const cell = { columnId: `c${i}`, displayValue: esc(raw) }
      const name = txt(raw)
      if (i === nameIdx && name && !name.startsWith('Total:') && name !== txt(headers[i])) {
        const num = numIdx >= 0 ? txt(r[numIdx]) : ''
        cell.displayValue = accountLinkHtml(accountsIndex, num ? `${num} ${raw}` : raw, esc(raw))
      }
      if (valueCol[i]) {
        const n = parseAmount(raw)
        if (n != null) cell.dataValue = n
      }
      return cell
    }),
  }))
  return { columns, reportData, reportFooterCells: [], treeColumnId: 'c2', unrecognised: true }
}

/**
 * Build the grid report for one layout.
 *
 *   single       [Assets, Liabilities, Equities] + the L&E grand total as the footer
 *   assets       [Assets]                         (side-by-side, left half)
 *   liabilities  [Liabilities, Equities] + L&E    (side-by-side, right half)
 *
 * so the two halves, joined in CB's order (exportViewMerge.js), ARE the single-column
 * statement. Level 0 = CB's side headings; level 1 = CB's sections. `Grand Total:
 * Assets` closes the Assets group (after the last asset section, where V1 prints Total
 * Assets). Liabilities has no footer (CB emits no Total Liabilities; neither does V1).
 * Equities is flat, as V1 prints it: every equity account in CB order — the nameless
 * combined section's rows, Retained Earnings, Net Income — closed by `Total: Equity`;
 * CB's per-section equity subtotals are not rendered (V1 renders none; the accounts
 * they total are all shown), and the blank $0.00 placeholders are hidden (Q2).
 *
 * Columns: Account Number (`c1`, LEFT, TEXT — no `dataValue`, ever), Account (`c2`,
 * LEFT, the tree column) and the value columns (RIGHT). Section (`c0`) and Parent
 * Account (`c3`) are read for classification and never shown or client-exported;
 * positional ids are kept so persisted widths / order / visibility / aliases apply.
 * Every total label renders in the Account column, verbatim ("Total: Bank").
 *
 * Roll-up (only when `model.rollup.status === 'ok'`): account rows carry `tree` keyed
 * on CB's account id; a parent's closer is its `close` row; a rollable parent's
 * `rolledCells` are its own Number and Name cells plus the CLOSER's value cells — CB's
 * own rolled figure (every category column + Total in category mode). cb2 sums nothing.
 *
 * @param model  buildBalanceSheetModel's result
 * @param {{ layout?: 'single'|'assets'|'liabilities', accountsIndex?: object }} opts
 * @returns the grid report, or null for a half of a statement that cannot be split
 */
export function toBalanceSheetGridReport(model, { layout = 'single', accountsIndex = null } = {}) {
  if (!model || model.empty) return { columns: [], reportData: [], reportFooterCells: [], treeColumnId: 'c2' }
  if (!model.ok) return layout === 'single' ? fallbackReport(model, accountsIndex) : null
  const { headers, cells, classes } = model
  const valueIdx = headers.map((_, i) => i).filter((i) => i >= 4)
  const aliasKeys = stableAliasKeys(headers)
  const columns = [1, 2, ...valueIdx].map((i) => ({
    columnId: `c${i}`,
    aliasKey: aliasKeys[i],
    label: headers[i],
    visible: true,
    columnAlignment: i < 4 ? 'LEFT' : 'RIGHT',
    // Account Number DECLARES its type: its cells look numeric ("10101"), so the
    // grid's row-evidence type (columnDataKinds) would otherwise call it a Number.
    ...(i === 1 ? { filterTypeName: 'TEXT' } : {}),
  }))

  const valueCells = (r) => valueIdx.map((i) => {
    const cell = { columnId: `c${i}`, displayValue: esc(r[i]) }
    const n = parseAmount(r[i])
    if (n != null) cell.dataValue = n
    return cell
  })
  // Account Number is TEXT: escaped, never a dataValue (spec 127 A2 / CC-53904).
  const numberCell = (raw) => ({ columnId: 'c1', displayValue: esc(raw) })
  const accountCells = (r) => {
    const name = r[2]
    const num = txt(r[1])
    return [
      numberCell(r[1]),
      // CC-49837: account rows deep-link to the register, resolved by number + name.
      { columnId: 'c2', displayValue: accountLinkHtml(accountsIndex, num ? `${num} ${name}` : name, esc(name)) },
      ...valueCells(r),
    ]
  }
  // A closer ("Total: Furniture and Equipment") and every section / statement total
  // put CB's label in the Account column; a section total's label is its column 0.
  const closerCells = (r) => [numberCell(r[1]), { columnId: 'c2', displayValue: esc(r[2]) }, ...valueCells(r)]
  const totalCells = (idx) => {
    const r = cells[idx]
    return [numberCell(''), { columnId: 'c2', displayValue: esc(txt(r[0])) }, ...valueCells(r)]
  }

  const h = model.rollup?.status === ROLLUP.OK ? model.rollup.rows : null
  const treeIdOf = (k) => `a:${h[k].accountId}`
  const parentIdOf = (k) => (h[k].parentAccountId != null && !h[k].parentElsewhere ? `a:${h[k].parentAccountId}` : null)
  const leaf = (k) => {
    const r = cells[k]
    const cls = classes[k]
    const row = { isGroup: false, dataCells: cls === ROW.CLOSER ? closerCells(r) : accountCells(r) }
    if (!h) return row
    const d = h[k]
    if (cls === ROW.ACCOUNT && d.accountId != null) {
      row.tree = { id: treeIdOf(k), parentId: parentIdOf(k), depth: d.depth, kind: 'node' }
      if (d.rollupRow != null) {
        const closer = cells[d.rollupRow]
        row.tree.rolledCells = [row.dataCells[0], row.dataCells[1], ...valueCells(closer)]
      }
    } else if (cls === ROW.CLOSER) {
      row.tree = { id: treeIdOf(d.ofRow), parentId: parentIdOf(d.ofRow), depth: d.depth, kind: 'close' }
    }
    return row
  }

  const sectionGroup = (s) => ({
    isGroup: true,
    groupHeaderContent: esc(s.label),
    dataRows: s.rows.map(leaf),
    groupFooterCells: totalCells(s.totalIdx),
  })
  const assetsGroup = {
    isGroup: true,
    groupHeaderContent: esc(model.assets.heading),
    dataRows: model.assets.sections.map(sectionGroup),
    groupFooterCells: totalCells(model.assets.totalIdx),
  }
  const liabilitiesGroup = {
    isGroup: true,
    groupHeaderContent: esc(model.liabilities.heading),
    dataRows: model.liabilities.sections.map(sectionGroup),
  }
  const equitiesGroup = {
    isGroup: true,
    groupHeaderContent: esc(model.equities.heading),
    dataRows: model.equities.rows.map(leaf),
    groupFooterCells: totalCells(model.equities.totalIdx),
  }
  const grand = totalCells(model.grandTotalIdx)
  if (layout === 'assets') return { columns, reportData: [assetsGroup], reportFooterCells: [], treeColumnId: 'c2' }
  if (layout === 'liabilities') {
    return { columns, reportData: [liabilitiesGroup, equitiesGroup], reportFooterCells: grand, treeColumnId: 'c2' }
  }
  return { columns, reportData: [assetsGroup, liabilitiesGroup, equitiesGroup], reportFooterCells: grand, treeColumnId: 'c2' }
}
